import { useTranslation } from 'react-i18next';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router';
import { Compass, RotateCw } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';

/**
 * The router's last net.
 *
 * Without it React Router renders its own developer notice — "Hey developer 👋
 * You can provide a way better UX than this" — and it does so in production
 * too, which is where a teacher met it.
 *
 * Two different things land here and must not be told the same way: a URL that
 * does not exist (the teacher mistyped, or followed a stale link — nothing is
 * broken) and a component that threw (something IS broken, and reloading is
 * worth a try). `isRouteErrorResponse` is what separates them.
 */
function ErrorShell({
  icon, title, body, children,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-50 px-6">
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-surface-100 text-surface-400">
          {icon}
        </div>
        <h1 className="text-xl font-semibold text-surface-900">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-surface-600">{body}</p>
        <div className="mt-6 flex justify-center gap-2">{children}</div>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation('common');
  return (
    <ErrorShell icon={<Compass className="h-6 w-6" />} title={t('notFound.title')} body={t('notFound.body')}>
      <Button asChild>
        <Link to="/">{t('notFound.cta')}</Link>
      </Button>
    </ErrorShell>
  );
}

export function RouteErrorPage() {
  const { t } = useTranslation('common');
  const error = useRouteError();

  // A 404 thrown as a route response is not a crash — say so honestly rather
  // than telling the teacher the app broke.
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;

  return (
    <ErrorShell icon={<RotateCw className="h-6 w-6" />} title={t('routeError.title')} body={t('routeError.body')}>
      <Button variant="outline" onClick={() => window.location.reload()}>
        {t('routeError.retry')}
      </Button>
      <Button asChild>
        <Link to="/">{t('notFound.cta')}</Link>
      </Button>
    </ErrorShell>
  );
}
